import { ProjectCard } from '../project';
import { getFiscalYearQuarterString, formatFiscalYear, getAllQuarters } from '../../../lib/fiscal-year';
import type { Product, AllQuartersData, ProjectEvaluation } from '../../../api/shared/types';

export interface QuarterCardProps {
  quarterInfo: ReturnType<typeof getAllQuarters>[0];
  selectedProduct: Product;
  quartersData: AllQuartersData;
  onCardClick: (projectName: string, fiscalYearQuarter: string) => void;
  projectEvaluation?: ProjectEvaluation | null;
  epicCountsLoading?: boolean;
  statusLoading?: boolean;
}

export function QuarterCard({ quarterInfo, selectedProduct, quartersData, onCardClick, projectEvaluation, epicCountsLoading, statusLoading }: QuarterCardProps) {
  const fiscalYearQuarter = `${formatFiscalYear(quarterInfo.fiscalYear)} ${quarterInfo.quarter}`;
  const fiscalYearQuarterKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);

  const quarterData = quartersData[fiscalYearQuarterKey];
  
  const data: {
    epicCount: number;
    status: 'not-started' | 'in-progress' | 'submitted' | 'approved' | 'rejected' | 'reopened' | null;
    capexPercentage?: number;
    rndPercentage?: number;
    submittedBy?: string;
    submittedByName?: string;
    submittedAt?: string;
    approvedBy?: string;
    approvedByName?: string;
    approvedAt?: string;
  } = quarterData ? {
    epicCount: quarterData.epicCount,
    status: (
      quarterData.status === 'reopened' || quarterData.status === 'withdrawn'
        ? 'reopened'
        : quarterData.status === 'pendingapproval'
        ? 'submitted'
        : quarterData.status === 'approved' || quarterData.status === 'archived'
        ? 'approved'
        : quarterData.status === 'rejected'
        ? 'rejected'
        : (quarterData.status === 'draft' || quarterData.status === 'inprogress') && quarterData.hasAssessedEpics
        ? 'in-progress'
        : 'not-started'
    ) as 'not-started' | 'in-progress' | 'submitted' | 'approved' | 'rejected' | 'reopened',
    capexPercentage: quarterData.capexPercentage,
    rndPercentage: quarterData.rndPercentage,
    submittedBy: quarterData.submittedBy,
    submittedByName: quarterData.submittedByName,
    submittedAt: quarterData.submittedAt,
    approvedBy: quarterData.approvedBy,
    approvedByName: quarterData.approvedByName,
    approvedAt: quarterData.approvedAt,
  } : {
    epicCount: 0,
    status: 'not-started',
  };

  const hasQuarterData = quarterData != null;
  const hasEpics = data.epicCount > 0;
  const hasProjectEvaluation = !!projectEvaluation;
  const isClickable = quarterInfo.isAvailable && hasQuarterData && selectedProduct.project_name != null && (hasEpics || hasProjectEvaluation);
  const disabledReason = quarterInfo.isAvailable && !hasQuarterData
    ? 'No project data available for this quarter'
    : quarterInfo.isAvailable && hasQuarterData && !hasEpics && !hasProjectEvaluation
    ? 'No closed items in this quarter'
    : undefined;

  return (
    <ProjectCard
      quarterInfo={quarterInfo}
      projectName={selectedProduct.project_name}
      data={data}
      onClick={
        isClickable
          ? () => onCardClick(selectedProduct.project_name, fiscalYearQuarter)
          : undefined
      }
      projectEvaluation={projectEvaluation}
      disabledReason={disabledReason}
      epicCountsLoading={epicCountsLoading}
      statusLoading={statusLoading}
    />
  );
}
